import React, { useState } from 'react';
import './style.css';
import CustomTable from '@components/Common/table/CustomTable';
import { TableDataFields, TableHeadCells } from './constant';
import { useModalDisclosure } from '@hooks/useDisclosure';
import { useAllUsersQuery, useVerifyUserMutation, useDeleteUserMutation } from '../../API/Dashboard';
import ConfirmMsg from '@components/Common/ConfirmDialog';
import { decryptData } from '@utils/EncryptDecrypt/EncryptDecrypt';
import { TableActionsMethod, User } from '@typing/global';
import EditUser from './EditUser';
import useDebounce from '@hooks/useDebounce';
import { defaultParams } from '@utils/global.constants';
import { failed, success } from '@components/Common/Toastify';
import { useNavigate } from 'react-router-dom';
import { Users } from 'lucide-react';

function UserList() {
  const [params, setParams] = useState(defaultParams);
  const navigate = useNavigate();
  const verifyUserMutation = useVerifyUserMutation({});
  const deleteUserMutation = useDeleteUserMutation({
    onSuccess: () => {
      refetch();
      success('User deleted successfully!');
    },
    onError: (error: any) => {
      failed(error?.response?.data?.message || 'Failed to delete user');
    },
  });

  const debouncedSearch = useDebounce(params.search, 500);
  const { isLoading, isFetching, data, refetch } = useAllUsersQuery({
    params: { ...params, search: debouncedSearch },
    config: { keepPreviousData: true },
  });

  const tableData = data?.data || [];

  // const updateUserMutation = useUpdateUserMutation({
  //   onSuccess: () => {
  //     refetch();
  //   },
  // });

  const { isOpen, close, open, data: userData } = useModalDisclosure();
  const confirmModal = useModalDisclosure();

  const handleTableActions = ({ action, data }: TableActionsMethod) => {
    switch (action) {
      case 'edit':
        open(data);
        break;
      case 'view':
        open(data);
        break;
      case 'delete':
        deleteUserMutation.mutate(String(data.id));
        break;
      case 'agents':
        const userName = data.firstname + ' ' + data.lastname || data.email || `User ${data.id}`;
        navigate(`/app/agents?userId=${data.id}&userName=${encodeURIComponent(userName)}`);
        break;
      case 'agent-analytics':
        const userNameAnalytics = data.firstname + ' ' + data.lastname || data.email || `User ${data.id}`;
        navigate(`/app/agents/analytics?userId=${data.id}&userName=${encodeURIComponent(userNameAnalytics)}`);
        break;
      default:
        break;
    }
  };

  const handleParamsChange = (key: string, value: string | number) => {
    setParams({ ...params, [key]: value });
  };


  const handleVerifyUser = (user: User) => {
    verifyUserMutation.mutate({
      user_id: user.id,
      isVerified: true,
    }, {
      onSuccess: () => {
        refetch();
        success('User verified successfully!');
      },
      onError: (error: any) => {
        failed('Something went wrong!');
      }
    });
  };

  return (
    <>
      <ConfirmMsg
        open={confirmModal.isOpen}
        title={'Warning'}
        message={
          <div>
            Are you sure, want to move{' '}
            <b style={{ textTransform: 'capitalize' }}>{decryptData(confirmModal.data?.fullName)}</b> from dashboard to
            the patient listing?
          </div>
        }
        showEntryName={false}
        onConfirm={() => {}}
        onDismiss={confirmModal.close}
        actionBtnTitle="Confirm"
      />
      <div className="main-dashboard-div">
        <div className="dashboard-wrapper">
          <div className="heading-dashboard">
            <div className="main-heading-div">
              <h2>User List</h2>
            </div>
          </div>
          <div className="main-heading-div">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search by name, email, or company..."
                className="search-input"
                value={params.search || ''}
                onChange={(e) => handleParamsChange('search', e.target.value)}
              />
            </div>
          </div>

          <div>
            <CustomTable
              tableData={tableData || []}
              headCells={TableHeadCells}
              dataFields={TableDataFields}
              canView={true}
              canDelete={true}
              canEdit={false}
              loading={isLoading}
              fetching={isFetching}
              totalPages={Math.ceil(data?.meta.total / params.pageSize) || 1}
              rowsPerPage={params.pageSize}
              currentPage={params.page}
              handlePageChange={(page) => page !== data?.meta.currentPage && handleParamsChange('page', page)}
              handleSort={(sortKeyOrder) => setParams({ ...params, ...sortKeyOrder, page: 1 })}
              selectedUserAction={handleTableActions}
              onVerifyUser={handleVerifyUser}
              showCustomAction={true}
              showAnalyticsAction={true}
            />
          </div>
        </div>
      </div>
      {isOpen ? (
        <EditUser
          isOpen={isOpen}
          data={userData}
          readOnly={userData.readOnly || true}
          title={`${userData.readOnly ? 'View User' : 'View User'}`}
          onClose={close}
          refetch={refetch}
        />
      ) : null}
    </>
  );
}

export default UserList;
